import React, { useRef, useState, useEffect, useCallback } from "react";
import cn from "classnames";
import { ScrollArea } from "../ScrollArea";
import { TableContext } from "./TableContext";

interface Props {
  children: React.ReactNode;
  className?: string;
  tableClassName?: string;
  striped?: boolean;
  hoverable?: boolean;
  fullWidth?: boolean;
  scrollable?: boolean;
  variant?: "default" | "plain";
  rounded?: boolean;
}

export function Table({
  children,
  className,
  tableClassName,
  striped = true,
  hoverable = true,
  fullWidth = true,
  scrollable = false,
  variant = "default",
  rounded = true,
}: Props) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);

  const checkScroll = useCallback(function checkScroll() {
    const el = viewportRef.current;
    if (!el) return;
    setHasHorizontalScroll(el.scrollWidth > el.clientWidth);
  }, []);

  useEffect(function observeScrollability() {
    if (!scrollable) return;
    const el = viewportRef.current;
    if (!el) return;
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);
    checkScroll();
    return () => observer.disconnect();
  }, [scrollable, checkScroll]);

  const tableElement = (
    <table className={cn("border-collapse text-sm leading-5 w-full", tableClassName)}>{children}</table>
  );

  return (
    <TableContext.Provider value={{ rounded, hasHorizontalScroll }}>
      <div
        className={cn(
          variant === "default" &&
            cn("border-2 surface-container-stroke bg-transparent table-fixed", rounded && "rounded-[20px]"),
          variant === "plain" && cn("border-0 bg-transparent", rounded && "rounded-[20px]"),
          fullWidth && (scrollable ? "w-full" : "min-w-full w-fit"),
          striped &&
            (scrollable
              ? "[&_table>tbody>tr:nth-child(even)]:bg-grey-50 dark:[&_table>tbody>tr:nth-child(even)]:bg-grey-900"
              : "[&>table>tbody>tr:nth-child(even)]:bg-grey-50 dark:[&>table>tbody>tr:nth-child(even)]:bg-grey-900"),
          hoverable &&
            (scrollable
              ? "[&_table>tbody>tr:hover]:bg-grey-50 dark:[&_table>tbody>tr:hover]:bg-grey-800! [&_table>thead>tr:hover]:!bg-inherit"
              : "[&>table>tbody>tr:hover]:bg-grey-50 dark:[&>table>tbody>tr:hover]:bg-grey-800! [&>table>thead>tr:hover]:!bg-inherit"),
          className,
        )}>
        {scrollable ? (
          <ScrollArea orientation="horizontal" viewportRef={viewportRef} type="always">
            {tableElement}
          </ScrollArea>
        ) : (
          tableElement
        )}
      </div>
    </TableContext.Provider>
  );
}
